import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Download } from "lucide-react";
import Logo from "@/components/Logo";

const LINKS = [
  { to: "/flappy-goal", label: "Flappy Goal", id: "flappy-goal" },
  { to: "/court-vision", label: "Court Vision", id: "court-vision" },
  { to: "/about", label: "About Us", id: "about" },
  { to: "/contact", label: "Contact Us", id: "contact" },
  { to: "/privacy", label: "Privacy Policy", id: "privacy" },
];

export default function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="fixed z-50 top-4 left-1/2 -translate-x-1/2 w-[92%] max-w-6xl rounded-2xl border border-mint/20 bg-pitch/70 backdrop-blur-xl shadow-2xl"
    >
      <div className="px-5 sm:px-6 py-3 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" onClick={() => setOpen(false)}>
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-7" data-testid="nav-desktop">
          {LINKS.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `text-sm font-semibold tracking-wide transition-colors duration-200 hover:text-volt ${
                  isActive ? "text-volt" : "text-slate-300"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/download"
            data-testid="nav-download-button"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-volt px-5 py-2 text-sm font-bold text-pitch transition-transform duration-200 hover:scale-105 active:scale-95"
          >
            <Download size={16} strokeWidth={2.5} />
            Download
          </Link>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-slate-200 hover:text-volt transition-colors"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden border-t border-line"
          >
            <div className="flex flex-col px-6 py-4 gap-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.id}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `text-base font-semibold ${isActive ? "text-volt" : "text-slate-300"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/download"
                data-testid="nav-mobile-download-button"
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-volt px-5 py-2.5 text-sm font-bold text-pitch"
              >
                <Download size={16} strokeWidth={2.5} />
                Download
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
